import { Title, Stack, Text } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconDownload, IconUpload } from "@tabler/icons-react"
import { Anchor, Button, Card, DEFAULT_MAX_SIZE, EqualWidthGrid } from "@tfg/ui"
import { Trans, useTranslation } from "react-i18next"

import { FindIngestionsFiltersDto } from "~/gen/api"
import { EcoModulationImportModal } from "~/pages/Brand/DataTransfer/Import/components/EcoModulationImportModal"
import { IngestionSortsType } from "~/pages/Brand/DataTransfer/Import/components/IngestionsSorts"
import { config } from "~/pages/Brand/DataTransfer/config.ts"
import { useHasEcoModulationsFeatureAccess } from "~/hooks/useHasEcoModulationsFeatureAccess"

type EcoModulationImportCardProps = {
  sorts: IngestionSortsType
  filters: FindIngestionsFiltersDto
}

export const EcoModulationImportCard = ({
  sorts,
  filters,
}: EcoModulationImportCardProps) => {
  const { t } = useTranslation("dataTransfer")
  const hasAccess = useHasEcoModulationsFeatureAccess()
  const [isModalOpen, { open: openModal, close: closeModal }] =
    useDisclosure(false)

  if (!hasAccess) {
    return null
  }

  return (
    <>
      <Card withBorder h="100%">
        <Stack gap="sm" h="100%">
          <Title order={3} fw={600}>
            {t("import.ecoModulation.templateCard.title")}
          </Title>
          <Text>
            <Trans
              i18nKey="dataTransfer:import.ecoModulation.templateCard.description"
              components={{
                a: (
                  <Anchor
                    size="xs"
                    td="underline"
                    href={t("import.ecoModulation.refashionUrl")}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
              }}
            />
          </Text>
        </Stack>
        <EqualWidthGrid>
          <Button
            icon={<IconDownload size={16} />}
            component="a"
            href={config.ECO_MODULATION_IMPORT_TEMPLATE}
            toolTip={t(
              "import.ecoModulation.templateCard.downloadButton.tooltip"
            )}
            variant="secondary"
            fullWidth
          >
            {t("import.ecoModulation.templateCard.downloadButton.label")}
          </Button>
          <Button
            icon={<IconUpload size={16} />}
            toolTip={t(
              "import.ecoModulation.templateCard.uploadButton.tooltip",
              {
                maxSize: DEFAULT_MAX_SIZE / 1024 ** 2,
              }
            )}
            variant="primary"
            fullWidth
            onClick={openModal}
          >
            {t("import.ecoModulation.templateCard.uploadButton.label")}
          </Button>
        </EqualWidthGrid>
      </Card>
      {/* Mount only while open so import-flow effects/queries do not run on page load */}
      {isModalOpen ? (
        <EcoModulationImportModal
          opened={isModalOpen}
          onClose={closeModal}
          sorts={sorts}
          filters={filters}
        />
      ) : null}
    </>
  )
}
